import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Bell, CheckCheck, Clock, AlertTriangle, Info, MessageSquare, Tag, Home } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

const typeConfig: Record<string, { icon: any; color: string; label: string }> = {
  lead_update: { icon: MessageSquare, color: 'text-blue-500', label: 'Inquiry Update' },
  price_change: { icon: Tag, color: 'text-green-500', label: 'Price Alert' },
  general: { icon: Info, color: 'text-muted-foreground', label: 'General' },
  system: { icon: AlertTriangle, color: 'text-amber-500', label: 'System' },
  property_update: { icon: Home, color: 'text-primary', label: 'Property' },
};

export function NotificationCenter() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('all');

  const { data: notifications, isLoading } = useQuery({
    queryKey: ['all-notifications', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(50);
      return data || [];
    },
    enabled: !!user,
  });

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from('notifications').update({ is_read: true }).eq('id', id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['all-notifications'] }),
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      await supabase.from('notifications').update({ is_read: true }).eq('user_id', user!.id).eq('is_read', false);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unread-notifications-count'] });
      toast.success('All notifications marked as read');
    },
  });

  const filtered = notifications?.filter(n => {
    if (tab === 'unread') return !n.is_read;
    if (tab === 'inquiries') return n.type === 'lead_update';
    if (tab === 'alerts') return n.type === 'price_change' || n.type === 'system';
    return true;
  }) || [];

  const unreadCount = notifications?.filter(n => !n.is_read).length || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            Notification Center
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {unreadCount > 0 ? `You have ${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" size="sm" onClick={() => markAllRead.mutate()} className="gap-2">
            <CheckCheck className="h-4 w-4" /> Mark All Read
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all">All ({notifications?.length || 0})</TabsTrigger>
          <TabsTrigger value="unread">
            Unread {unreadCount > 0 && <Badge variant="destructive" className="ml-1 h-5 text-[10px]">{unreadCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="inquiries">Inquiries</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">Loading notifications...</div>
          ) : filtered.length === 0 ? (
            <Card className="rounded-2xl">
              <CardContent className="p-12 text-center">
                <Bell className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground font-medium">No notifications here</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {tab === 'unread' ? 'All caught up! No unread notifications.' : 'Notifications will appear here as you use the platform.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filtered.map((notif) => {
                const config = typeConfig[notif.type] || typeConfig.general;
                const Icon = config.icon;
                return (
                  <Card
                    key={notif.id}
                    className={`rounded-xl cursor-pointer transition-all hover:shadow-sm ${!notif.is_read ? 'border-primary/20 bg-primary/5' : ''}`}
                    onClick={() => !notif.is_read && markRead.mutate(notif.id)}
                  >
                    <CardContent className="p-4 flex items-start gap-4">
                      <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${!notif.is_read ? 'bg-primary/10' : 'bg-muted'}`}>
                        <Icon className={`h-5 w-5 ${config.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold flex items-center gap-2">
                              {notif.title}
                              {!notif.is_read && <span className="h-2 w-2 rounded-full bg-primary inline-block" />}
                            </div>
                            <Badge variant="outline" className="text-[10px] mt-1">{config.label}</Badge>
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        {notif.message && (
                          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{notif.message}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
